import { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Table,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  FormGroup,
  Label
} from 'reactstrap';
import { toast } from 'react-toastify';
import { getPlans, createPlan, updatePlan, deletePlan } from '../../../axios/subscription';

const SquarePlanCreation = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Selected plan
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Add/Edit Plan Fields
  const [planName, setPlanName] = useState('');
  const [planDescription, setPlanDescription] = useState('');
  const [planType, setPlanType] = useState('Starter');
  const [priceMonthly, setPriceMonthly] = useState('');
  const [priceAnnual, setPriceAnnual] = useState('');
  const [planCurrency, setPlanCurrency] = useState('GBP');

  // Fetch Plans
  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await getPlans();
      if (response.code === 0 && Array.isArray(response.data)) {
        setPlans(response.data);
      } else {
        toast.error('Failed to load plans.');
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Error fetching plans.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // Toggle Modals
  const toggleAddModal = () => setIsAddModalOpen(!isAddModalOpen);
  const toggleEditModal = (plan = null) => {
    if (plan) {
      setSelectedPlan(plan);
      setPlanName(plan.name);
      setPlanDescription(plan.description);
      setPlanType(plan.type);
      setPriceMonthly(plan.priceMonthly);
      setPriceAnnual(plan.priceAnnual);
      setPlanCurrency(plan.currency);
    }
    setIsEditModalOpen(!isEditModalOpen);
  };
  const toggleDeleteModal = (plan = null) => {
    setSelectedPlan(plan);
    setIsDeleteModalOpen(!isDeleteModalOpen);
  };

  // Create Plan
  const handleAddPlan = async () => {
    if (!planName.trim() || !planDescription.trim() || !priceMonthly || !priceAnnual) {
      toast.error('Please fill all fields.');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        name: planName,
        description: planDescription,
        type: planType,
        priceMonthly: Number(priceMonthly),
        priceAnnual: Number(priceAnnual),
        currency: planCurrency
      };

      const response = await createPlan(payload);
      if (response.message === 'success') {
        toast.success('Plan created successfully!');
        toggleAddModal();
        fetchPlans();
        resetForm();
      } else {
        toast.error(response.message || 'Failed to create plan.');
      }
    } catch (error) {
      console.error('Error creating plan:', error);
      toast.error('Error creating plan.');
    } finally {
      setLoading(false);
    }
  };

  // Update Plan
  const handleUpdatePlan = async () => {
    if (!selectedPlan) return;
    try {
      setLoading(true);
      const payload = {
        id: selectedPlan.id,
        name: planName,
        description: planDescription,
        type: planType,
        priceMonthly: Number(priceMonthly),
        priceAnnual: Number(priceAnnual),
        currency: planCurrency
      };

      const response = await updatePlan(payload);
      if (response.message === 'success') {
        toast.success(response.data ?? 'Plan updated');
        toggleEditModal();
        fetchPlans();
      } else {
        toast.error(response.message || 'Failed to update plan.');
      }
    } catch (error) {
      console.error('Error updating plan:', error);
      toast.error('Error updating plan.');
    } finally {
      setLoading(false);
    }
  };

  // Delete Plan
  const handleDeletePlan = async () => {
    if (!selectedPlan) return;
    try {
      setLoading(true);
      const response = await deletePlan(selectedPlan.id);
      if (response.message === 'success') {
        toast.success('Plan deleted successfully!');
        toggleDeleteModal();
        fetchPlans();
      } else {
        toast.error(response.message || 'Failed to delete plan.');
      }
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast.error('Error deleting plan.');
    } finally {
      setLoading(false);
    }
  };

  // Reset Form
  const resetForm = () => {
    setPlanName('');
    setPlanDescription('');
    setPlanType('Starter');
    setPriceMonthly('');
    setPriceAnnual('');
    setPlanCurrency('GBP');
  };

  return (
    <Container fluid>
      <Row>
        <Col>
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <h3 className="mb-0">Subscription Plans</h3>
              <Button color="primary" size="sm" onClick={toggleAddModal}>
                + Add Plan
              </Button>
            </CardHeader>

            <CardBody>
              {loading ? (
                <div className="text-center py-5">Loading...</div>
              ) : plans.length === 0 ? (
                <div className="text-center text-muted py-5">No plans found.</div>
              ) : (
                <Table responsive bordered hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Type</th>
                      <th>Monthly</th>
                      <th>Annual</th>
                      <th>Currency</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plans.map((plan, i) => (
                      <tr key={plan.id}>
                        <td>{i + 1}</td>
                        <td>
                          <strong>{plan.name}</strong>
                        </td>
                        <td>{plan.description}</td>
                        <td>
                          <Badge
                            color={
                              plan.type === 'Pro'
                                ? 'primary'
                                : plan.type === 'Enterprise'
                                  ? 'warning'
                                  : 'success'
                            }
                          >
                            {plan.type}
                          </Badge>
                        </td>
                        <td>£{plan.priceMonthly}</td>
                        <td>£{plan.priceAnnual}</td>
                        <td>{plan.currency}</td>
                        <td className="d-flex gap-2">
                          <Button color="light" size="sm" onClick={() => toggleDeleteModal(plan)}>
                            Delete
                          </Button>
                          <Button
                            color="primary"
                            size="sm"
                            className="me-2"
                            onClick={() => toggleEditModal(plan)}
                          >
                            Edit
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Add Plan Modal */}
      <Modal isOpen={isAddModalOpen} toggle={toggleAddModal}>
        <ModalHeader toggle={toggleAddModal}>Add New Plan</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Name</Label>
            <Input
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              placeholder="Enter plan name"
            />
          </FormGroup>

          <FormGroup>
            <Label>Description</Label>
            <Input
              type="textarea"
              value={planDescription}
              onChange={(e) => setPlanDescription(e.target.value)}
              placeholder="Enter description"
            />
          </FormGroup>

          <FormGroup>
            <Label>Type</Label>
            <Input type="select" value={planType} onChange={(e) => setPlanType(e.target.value)}>
              <option value="Starter">Starter</option>
              <option value="Pro">Pro</option>
              <option value="Enterprise">Enterprise</option>
            </Input>
          </FormGroup>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Monthly Price</Label>
                <Input
                  type="number"
                  value={priceMonthly}
                  onChange={(e) => setPriceMonthly(e.target.value)}
                  placeholder="e.g., 10"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Annual Price</Label>
                <Input
                  type="number"
                  value={priceAnnual}
                  onChange={(e) => setPriceAnnual(e.target.value)}
                  placeholder="e.g., 100"
                />
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label>Currency</Label>
            <Input
              type="select"
              value={planCurrency}
              onChange={(e) => setPlanCurrency(e.target.value)}
            >
              <option value="GBP">GBP</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
            </Input>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleAddModal}>
            Cancel
          </Button>
          <Button color="success" onClick={handleAddPlan} disabled={loading}>
            {loading ? 'Creating...' : 'Create Plan'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Plan Modal */}
      <Modal isOpen={isEditModalOpen} toggle={toggleEditModal}>
        <ModalHeader toggle={toggleEditModal}>Edit Plan</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Name</Label>
            <Input value={planName} onChange={(e) => setPlanName(e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>Description</Label>
            <Input
              type="textarea"
              value={planDescription}
              onChange={(e) => setPlanDescription(e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label>Type</Label>
            <Input type="select" value={planType} onChange={(e) => setPlanType(e.target.value)}>
              <option value="Starter">Starter</option>
              <option value="Pro">Pro</option>
              <option value="Enterprise">Enterprise</option>
            </Input>
          </FormGroup>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Monthly Price</Label>
                <Input
                  type="number"
                  value={priceMonthly}
                  onChange={(e) => setPriceMonthly(e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Annual Price</Label>
                <Input
                  type="number"
                  value={priceAnnual}
                  onChange={(e) => setPriceAnnual(e.target.value)}
                />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label>Currency</Label>
            <Input
              type="select"
              value={planCurrency}
              onChange={(e) => setPlanCurrency(e.target.value)}
            >
              <option value="GBP">GBP</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
            </Input>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleEditModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleUpdatePlan} disabled={loading}>
            {loading ? 'Updating...' : 'Save Changes'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} toggle={toggleDeleteModal}>
        <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
        <ModalBody>
          Are you sure you want to delete <strong>{selectedPlan?.name}</strong>?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleDeleteModal}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDeletePlan} disabled={loading}>
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default SquarePlanCreation;
